import React, { useState, useEffect, useRef, useMemo } from "react";
import "./Themes.scss";
import VectorSun from "./vectors/VectorSun";
import VectorCloud from "./vectors/VectorCloud";
import VectorSea from "./vectors/VectorSea";
import VectorStarfish from "./vectors/VectorStarfish";
import VectorShell from "./vectors/VectorShell";
import VectorUmbrella from "./vectors/VectorUmbrella";
import VectorHotAirBalloon from "./vectors/VectorHotAirBalloon";
import VectorLifeRing from "./vectors/VectorLifeRing";
import VectorSand from "./vectors/VectorSand";
import VectorCoconutTree from "./vectors/VectorCoconutTree";
import VectorBeachMat from "./vectors/VectorBeachMat";
import VectorGrass from "./vectors/VectorGrass";

const ThemeSummer = ({ children }: { children?: React.ReactNode }) => {
    const [isRaining, setIsRaining] = useState(false);
    const sunRef = useRef<SVGSVGElement>(null);
    const cloudsContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Run rain effect every 24 seconds, lasting for 12 seconds
        const rainInterval = setInterval(() => {
            setIsRaining(true);
            setTimeout(() => {
                setIsRaining(false);
            }, 12_000); // Stop raining after 12 seconds
        }, 24_000); // Every 24 seconds

        return () => clearInterval(rainInterval);
    }, []);

    // Generate random raindrops - useMemo to prevent recreating on every render
    const rainDrops = useMemo(() => Array.from({ length: 50 }).map((_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 2}s`,
        animationDuration: `${0.5 + Math.random() * 0.5}s`
    })), []);

    return (
        <div className={`is-theme-summer ${isRaining ? 'is-raining' : ''}`}>
            {children}
            <div className={`shadow-overlay ${isRaining ? 'is-active' : ''}`} />

            {/* Rain Drops Container */}
            <div className={`rain-container ${isRaining ? 'is-raining' : ''}`}>
                {rainDrops.map((drop) => (
                    <div
                        key={drop.id}
                        className="raindrop"
                        style={{
                            left: drop.left,
                            animationDelay: drop.animationDelay,
                            animationDuration: drop.animationDuration
                        }}
                    ></div>
                ))}
            </div>

            <div className="nav-body-vectors">

                {/* Sun */}
                <VectorSun ref={sunRef} className="vector-sun" />

                <div ref={cloudsContainerRef} className="clouds-container" style={{ position: 'absolute', width: '100%', height: '100%' }}>
                    <VectorCloud className="vector-cloud cloud-1" variant={2} />
                    <VectorCloud className="vector-cloud cloud-2" variant={1} />
                    <VectorCloud className="vector-cloud cloud-3" variant={3} />
                    <VectorCloud className="vector-cloud cloud-4" variant={2} />
                    <VectorCloud className="vector-cloud cloud-5" variant={1} />
                    <VectorCloud className="vector-cloud cloud-6" variant={4} />
                    <VectorCloud className="vector-cloud cloud-7" variant={2} />
                    <VectorCloud className="vector-cloud cloud-8" variant={3} />
                    <VectorCloud className="vector-cloud cloud-9" variant={4} />
                    <VectorCloud className="vector-cloud cloud-10" variant={2} />
                    <VectorCloud className="vector-cloud cloud-11" variant={3} />
                </div>

                {/* Sea */}
                <VectorSea className="vector-sea" />

                {/* Sand */}
                <VectorSand className="vector-sand" />

                {/* Corner Coconut Trees - Left Group */}
                <VectorCoconutTree className="vector-coconut-tree corner-left" />
                <VectorCoconutTree className="vector-coconut-tree corner-left-2" />
                <VectorCoconutTree className="vector-coconut-tree corner-left-3" />
                <VectorGrass className="vector-grass grass-left" />

                {/* Corner Coconut Trees - Right Group */}
                <VectorCoconutTree className="vector-coconut-tree corner-right" />
                <VectorCoconutTree className="vector-coconut-tree corner-right-2" />
                <VectorGrass className="vector-grass grass-right" />

                {/* Beach Items */}
                <VectorLifeRing className="vector-life-ring" />
                <VectorStarfish className="vector-starfish starfish-1" />
                <VectorStarfish className="vector-starfish starfish-2" />
                <VectorShell className="vector-shell shell-1" />
                <VectorShell className="vector-shell shell-2" />
                <VectorUmbrella className="vector-umbrella" />
                <VectorBeachMat className="vector-beach-mat" />

                {/* Hot Air Balloons */}
                <VectorHotAirBalloon className="vector-hot-air-balloon balloon-1" variant={1} />
                <VectorHotAirBalloon className="vector-hot-air-balloon balloon-2" variant={2} />

            </div>
        </div>
    );
};

export default ThemeSummer;
